<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";

type BulletSection = {
  title: string;
  items: string[];
  layout?: "list" | "grid";
};

type TextSection = {
  title: string;
  items: Array<{
    title: string;
    description: string;
  }>;
};

type McqSection = {
  title: string;
  items: Array<{
    question: string;
    options: Array<{
      label: string;
      text: string;
      verdict: string;
      reason: string;
    }>;
  }>;
};

defineProps<{
  unit: string;
  title: string;
  intro: string;
  bulletSections: BulletSection[];
  textSections?: TextSection[];
  mcqSections?: McqSection[];
}>();
</script>

<template>
  <PageContainer>
    <EnglishHero :headline="unit" :title="title" :description="intro" />

    <template v-for="section in bulletSections" :key="section.title">
      <EnglishSection :title="section.title" :description="`${section.items.length} key points`">
        <EnglishGrid v-if="section.layout === 'grid'">
          <EnglishCard
            v-for="item in section.items"
            :key="item"
            title="Point"
            :description="item"
          />
        </EnglishGrid>
        <EnglishGrid v-else>
          <EnglishCard
            v-for="item in section.items"
            :key="item"
            title="Note"
            :description="item"
          />
        </EnglishGrid>
      </EnglishSection>
    </template>

    <EnglishSection
      v-for="section in textSections"
      :key="section.title"
      :title="section.title"
      :description="`${section.items.length} prepared entries`"
    >
      <EnglishGrid>
        <EnglishCard
          v-for="item in section.items"
          :key="item.title"
          :title="item.title"
          :description="item.description"
        />
      </EnglishGrid>
    </EnglishSection>

    <EnglishSection
      v-for="section in mcqSections"
      :key="section.title"
      :title="section.title"
      :description="`${section.items.length} explained MCQs`"
    >
      <EnglishGrid>
        <EnglishCard
          v-for="item in section.items"
          :key="item.question"
          :title="item.question"
          :description="item.options.map((option) => `${option.label}. ${option.text} ${option.verdict === 'correct' ? 'Correct:' : 'Not correct:'} ${option.reason}`).join(' ')"
        />
      </EnglishGrid>
    </EnglishSection>
  </PageContainer>
</template>
